/* Piobox catalogue (2026-09-29). Reads the cards pb-catalogue.liquid renders and builds:
 *   · colour swatches (name → hex, two-tone names split), swatch → that colour's photo
 *   · the filter rail, one group per kind of data this collection actually has
 *   · Match my Pantone: nearest garment dye by ΔE (CIE76), cards re-sorted by closeness
 *   · sort, Show more, URL state (?fam=nvy&size=4XL…), mobile filter sheet, add to quote (PBQ)
 * Colour hexes and PMS values are screen estimates for browsing, never a dye match. */
(() => {
  const SELF_SRC = (document.currentScript && document.currentScript.src) || "";
  const root = document.querySelector("[data-pbc]");
  if (!root || root.dataset.ready) return;
  root.dataset.ready = "1";
  const $ = (s, el = root) => el.querySelector(s);
  const $$ = (s, el = root) => [...el.querySelectorAll(s)];
  /* 2026-10-11 mobile fixes: write to the DOM only when something changed. Moving every card and swatch on each
     filter tap cost ~1 s on a phone (layout + PostHog replay re-serialising every moved node). */
  const setText = (el, t) => { if (el && el.textContent !== String(t)) el.textContent = t; };
  const setHTML = (el, x) => { if (el && el.__pbcH !== x) { el.innerHTML = x; el.__pbcH = x; } };
  const setHidden = (el, v) => { if (el && el.hidden !== !!v) el.hidden = !!v; };
  // The your-logo zone is hidden at 900px and below (pb-catalogue.css), so phones skip the photo measuring;
  // it runs (when idle) the first time the zones become visible.
  const zoneMQ = window.matchMedia("(max-width: 900px)");
  const zonesShown = () => !zoneMQ.matches;
  const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 600 }) : setTimeout(fn, 1));
  const h = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

  /* ── colour system: name → [hex, family, ≈PMS] ── */
  const C = {
    "Black": ["#1c1c1c", "blk", "Black 6 C"], "Charcoal": ["#3b3e43", "blk", "432 C"], "Graphite Grey": ["#4a4d52", "blk", "7540 C"], "Charcoal Melange": ["#57595e", "blk", "425 C"],
    "White": ["#ffffff", "wht", "White"], "Ivory": ["#f2ead8", "wht", "Warm Gray 1 C"], "Cream": ["#f4ecd6", "wht", "7527 C"], "Oatmeal Melange": ["#dcd2bf", "wht", "7527 C"], "Clear": ["#eef0f0", "wht", ""],
    "Grey": ["#8e9196", "gry", "Cool Gray 8 C"], "Grey Melange": ["#b9bbbe", "gry", "Cool Gray 4 C"], "Slate Grey": ["#5f6a73", "gry", "7545 C"], "Dark Grey": ["#4d5054", "gry", "Cool Gray 11 C"], "Steel Grey": ["#6e7780", "gry", "7544 C"], "Silver": ["#c0c3c7", "gry", "877 C"],
    "Navy": ["#1d2540", "nvy", "289 C"], "Navy Melange": ["#2f3855", "nvy", "2767 C"],
    "Royal": ["#1f4fb4", "blu", "2728 C"], "Airforce Blue": ["#5b7ea3", "blu", "5405 C"], "Sky Blue": ["#86c3e8", "blu", "292 C"], "Powder Blue": ["#aecbe3", "blu", "277 C"], "Powder": ["#aecbe3", "blu", "277 C"], "Royal Melange": ["#3b5fa8", "blu", "7684 C"], "Cobalt Blue": ["#1f48c4", "blu", "2728 C"], "Denim": ["#4a6284", "blu", "7699 C"], "Dark Denim": ["#2e3f5c", "blu", "2767 C"], "Medium Denim": ["#5a7aa3", "blu", "7453 C"],
    "Turquoise": ["#13a2b6", "tea", "3135 C"], "Teal Blue": ["#1e6d78", "tea", "3155 C"], "Aqua": ["#6fd0d4", "tea", "3242 C"],
    "Bottle": ["#1f4a34", "grn", "3435 C"], "Bottle Melange": ["#35584a", "grn", "5535 C"], "Emerald": ["#0c8a4f", "grn", "348 C"], "Dark Green": ["#243d2c", "grn", "553 C"], "Green": ["#2e8b3a", "grn", "356 C"], "Kelly Green": ["#1f9a4b", "grn", "355 C"], "SA Green": ["#007a4d", "grn", "348 C"], "Olive": ["#5b5a30", "grn", "5815 C"], "Army Green": ["#4b5320", "grn", "5747 C"], "Military Green": ["#4b5a36", "grn", "5743 C"], "Military Melange": ["#626349", "grn", "5747 C"], "Sage Green": ["#9aae8f", "grn", "5575 C"], "Sage": ["#9aae8f", "grn", "5575 C"], "Mint Green": ["#a7dbbf", "grn", "344 C"], "Lime": ["#8cc63f", "grn", "368 C"],
    "Red": ["#c62130", "red", "186 C"], "Coral Red": ["#f2555a", "red", "178 C"], "Burgundy": ["#6a1f2d", "red", "7421 C"], "Burgundy Melange": ["#7a3542", "red", "7645 C"],
    "Pink": ["#f08ab1", "pnk", "204 C"], "Dusty Pink": ["#d7a4a3", "pnk", "7612 C"], "Fuchsia": ["#c1217a", "pnk", "227 C"], "Hot Pink": ["#ff3d8b", "pnk", "213 C"], "Raspberry": ["#b3245a", "pnk", "207 C"], "Pink Melange": ["#e7a3bd", "pnk", "7432 C"], "Coral Melange": ["#ea8a7c", "pnk", "7416 C"], "Lilac": ["#b8a4d5", "pnk", "2635 C"], "Purple": ["#5a2d82", "pnk", "2685 C"],
    "Orange": ["#ee6a1f", "org", "1655 C"], "Burnt Orange": ["#c1531b", "org", "7580 C"], "Yellow": ["#f4d00f", "org", "116 C"], "Lemon": ["#f3e88a", "org", "100 C"], "Mustard": ["#d09a2a", "org", "7555 C"], "Gold": ["#c8a24a", "org", "7555 C"], "Peach": ["#f4b999", "org", "162 C"],
    "Stone": ["#cfc3a7", "nat", "7501 C"], "Sand": ["#d8c7a3", "nat", "7502 C"], "Khaki": ["#b29e75", "nat", "4525 C"], "Dark Khaki": ["#8b7d57", "nat", "872 C"], "Tan": ["#c2a27c", "nat", "7503 C"], "Camel": ["#c19a6b", "nat", "7510 C"], "Taupe": ["#8b7b6e", "nat", "Warm Gray 10 C"], "Tobacco": ["#8a5a35", "nat", "7511 C"], "Coffee": ["#6f4e37", "nat", "7568 C"], "Army Brown": ["#6b5a3e", "nat", "7533 C"], "Choc": ["#4a3326", "nat", "4625 C"],
    /* 2026-10-09 catalogue clean-up: colours that showed a plain grey swatch */
    "Blue": ["#2f5fb3", "blu", "2935 C"], "Navy Blue": ["#1f2a4a", "nvy", "289 C"], "French Navy": ["#232c4f", "nvy", "2768 C"], "Indigo Navy": ["#26305a", "nvy", "2766 C"], "Sky": ["#86c3e8", "blu", "292 C"], "Sea Blue": ["#2f7fa8", "blu", "7690 C"], "Light Denim": ["#7d98b8", "blu", "7543 C"], "Aqua Blue": ["#5fc4d6", "tea", "3105 C"], "Mint Turquoise": ["#7fd3c3", "tea", "3245 C"],
    "Brown": ["#6b4a2f", "nat", "4635 C"], "Dark Brown": ["#4a3326", "nat", "4625 C"], "Chocolate": ["#4a3326", "nat", "4625 C"], "Natural": ["#e8dcc4", "wht", "7527 C"],
    "Maroon": ["#5e1a28", "red", "7421 C"], "Rust": ["#a0482a", "org", "7599 C"], "Sunflower": ["#f2b705", "org", "7549 C"],
    "Heather Grey": ["#a7a9ac", "gry", "Cool Gray 6 C"], "Light Grey Melange": ["#cfd1d3", "gry", "Cool Gray 2 C"],
    "Cerise": ["#c8166a", "pnk", "213 C"], "Cerise Pink": ["#c8166a", "pnk", "213 C"], "Fuchsia Pink": ["#c1217a", "pnk", "227 C"], "Coral Pink": ["#f28a8a", "pnk", "177 C"], "Light Pink": ["#f6c6d3", "pnk", "182 C"],
    "Heritage Green": ["#2f5d46", "grn", "3425 C"], "Moss": ["#5d6b3a", "grn", "5767 C"], "Leaf": ["#4f7a3a", "grn", "7743 C"], "Jungle": ["#2f4a2a", "grn", "5535 C"], "Fatigue": ["#5e5f43", "grn", "5743 C"], "Forest Green": ["#2f4a2e", "grn", "5535 C"], "Forest": ["#2f4a2e", "grn", "5535 C"], "Cyan": ["#00a3e0", "tea", "299 C"],
    "Lumo Orange": ["#ff7a1a", "viz", "804 C"], "Lumo Yellow": ["#e4ff2e", "viz", "803 C"], "Cyber Orange": ["#ff6a13", "viz", "804 C"], "Cyber Yellow": ["#ffe600", "viz", "803 C"], "Cyber Lime": ["#c8ff00", "viz", "802 C"],
  };
  const PRINTS = { "Camo": ["#6b6a45", "#3f4a2e", "#a39a6b"], "Autumn Camo": ["#8a5a35", "#5b5a30", "#c19a6b"], "Black Camo": ["#1c1c1c", "#4d5054", "#8e9196"], "Blue Camo": ["#1d2540", "#5b7ea3", "#aecbe3"], "Brown Camo": ["#4a3326", "#8a5a35", "#b29e75"], "Pink Camo": ["#f08ab1", "#c1217a", "#f2ead8"], "Forrest Camo": ["#3f4a2e", "#5b5a30", "#8b7d57"], "Leaflock": ["#4b5320", "#8b7d57", "#2e3a22"], "Oakshade": ["#6b5a3e", "#4b5a36", "#a39a6b"], "Rainbow": ["#c62130", "#f4d00f", "#1f4fb4"], "Swirl": ["#5a2d82", "#13a2b6", "#f08ab1"], "S.A Flag": ["#007a4d", "#ffb81c", "#c8102e", "#001489"], "South Africa": ["#007a4d", "#ffb81c", "#1c1c1c"], "South Africa Arrow": ["#007a4d", "#ffb81c", "#c8102e"], "Forest Camo": ["#3f4a2e", "#5b5a30", "#8b7d57"], "SA Flag": ["#007a4d", "#ffb81c", "#c8102e", "#001489"], "Snow Camo": ["#f2f2f2", "#b9bbbe", "#6e7780"], "Vintage Camo": ["#6b6a45", "#a39a6b", "#8b7d57"], "Camo Brown": ["#4a3326", "#8a5a35", "#b29e75"], "Camo Green": ["#3f4a2e", "#5b5a30", "#8b7d57"], "Camo Leaf": ["#4f7a3a", "#3f4a2e", "#a39a6b"], "Camo Oak": ["#6b5a3e", "#4b5a36", "#a39a6b"], "Camo Pine": ["#2f4a2a", "#4b5320", "#6b5a3e"], "Camo Thorn": ["#8b7b6e", "#5b5a30", "#cfc3a7"], "Camo Charcoal": ["#3b3e43", "#6e7780", "#1c1c1c"], "Tropical": ["#1f9a4b", "#f4d00f", "#ee6a1f"], "Aztec": ["#c1531b", "#1d2540", "#f2ead8"], "Fruit": ["#c62130", "#f4d00f", "#8cc63f"], "Stripe": ["#1d2540", "#ffffff", "#c62130"], "Vintage": ["#8b7b6e", "#cfc3a7", "#5f6a73"] };
  const FAMS = [["blk", "Black", "#1c1c1c"], ["wht", "White & cream", "#ffffff"], ["gry", "Grey", "#b9bbbe"], ["nvy", "Navy", "#1d2540"], ["blu", "Blue", "#1f4fb4"], ["tea", "Teal & aqua", "#13a2b6"], ["grn", "Green", "#1f4a34"], ["red", "Red & wine", "#c62130"], ["pnk", "Pink & purple", "#c1217a"], ["org", "Orange & yellow", "#ee6a1f"], ["nat", "Neutral & brown", "#b29e75"], ["viz", "Hi-vis & print", null]];
  const FAM_BG = { viz: "conic-gradient(#ff7a1a 0 25%, #e4ff2e 0 50%, #6b6a45 0 75%, #c1217a 0)" };
  const KEYS = Object.keys(C).sort((a, b) => b.length - a.length);

  const colourCache = {};
  function colour(name) {
    if (colourCache[name]) return colourCache[name];
    let out;
    if (C[name]) out = { hexes: [C[name][0]], fam: C[name][1], pms: C[name][2] };
    else if (PRINTS[name]) out = { hexes: PRINTS[name], fam: "viz", pms: "", print: true };
    else {
      // two- and three-tone names ("Navy White Burgundy", "Forrest Camo Black"): longest known words first
      // "Khaki/Black" and "Khaki / Black" read the same as "Khaki Black"
      let rest = name.replace(/\s*\/\s*/g, " ").replace(/\s+/g, " ").trim(); const hexes = []; let fam = null, guard = 0;
      while (rest && guard++ < 6) {
        const k = [...KEYS, ...Object.keys(PRINTS)].find((key) => rest === key || rest.startsWith(key + " "));
        if (!k) break;
        const c = colour(k);
        hexes.push(c.hexes[0]); fam = fam || c.fam;
        rest = rest.slice(k.length).trim();
      }
      out = hexes.length ? { hexes, fam, pms: "", multi: hexes.length > 1 } : { hexes: ["#c9c9c9"], fam: null, pms: "" };
    }
    return (colourCache[name] = out);
  }
  const paintBg = (c) => c.hexes.length === 1 ? c.hexes[0]
    : c.print ? `conic-gradient(${c.hexes.map((x, i, a) => `${x} ${(i / a.length) * 100}% ${((i + 1) / a.length) * 100}%`).join(", ")})`
    : `linear-gradient(135deg, ${c.hexes.map((x, i, a) => `${x} ${(i / a.length) * 100}% ${((i + 1) / a.length) * 100}%`).join(", ")})`;

  /* ── Pantone lookup (common brand coated values, sRGB) ── */
  const PMS = {
    "100 C": "#F6EB61", "102 C": "#FCE300", "109 C": "#FFD100", "116 C": "#FFCD00", "123 C": "#FFC72C", "130 C": "#F2A900", "137 C": "#FFA300", "143 C": "#F1B434", "151 C": "#FF8200", "158 C": "#E87722", "165 C": "#FF671F", "172 C": "#FA4616", "178 C": "#FF585D", "179 C": "#E03C31", "185 C": "#E4002B", "186 C": "#C8102E", "187 C": "#A6192E", "192 C": "#E40046", "199 C": "#D50032", "200 C": "#BA0C2F", "201 C": "#9D2235", "202 C": "#862633", "206 C": "#CE0037", "213 C": "#E31C79", "219 C": "#DA1884", "225 C": "#DF1995", "226 C": "#D0006F", "232 C": "#E93CAC", "253 C": "#AD1AAC", "265 C": "#9063CD", "268 C": "#582C83", "2685 C": "#330072", "2728 C": "#0047BB", "280 C": "#012169", "281 C": "#00205B", "286 C": "#0033A0", "287 C": "#003087", "288 C": "#002D72", "289 C": "#0C2340", "293 C": "#003DA5", "294 C": "#002F6C", "295 C": "#002855", "299 C": "#00A3E0", "300 C": "#005EB8", "301 C": "#004B87", "3005 C": "#0077C8", "2925 C": "#009CDE", "2995 C": "#00A9E0", "306 C": "#00B5E2", "3125 C": "#00AEC7", "320 C": "#009CA6", "3278 C": "#009B77", "3292 C": "#004F43", "3415 C": "#007749", "3425 C": "#006847", "3435 C": "#154734", "347 C": "#009A44", "348 C": "#00843D", "349 C": "#046A38", "354 C": "#00B140", "355 C": "#009639", "356 C": "#007A33", "361 C": "#43B02A", "368 C": "#78BE20", "375 C": "#97D700", "376 C": "#84BD00", "382 C": "#C4D600", "021 C": "#FE5000", "012 C": "#FFD700", "032 C": "#EF3340", "072 C": "#10069F", "1235 C": "#FFB81C", "1375 C": "#FF9E1B", "1505 C": "#FF6900", "1655 C": "#FC4C02", "1665 C": "#DC4405", "1788 C": "#EE2737", "1795 C": "#D22630", "484 C": "#9A3324", "485 C": "#DA291C", "7406 C": "#F1C400", "7408 C": "#F6BE00", "7421 C": "#651D32", "7427 C": "#97233F", "7462 C": "#00558C", "7545 C": "#425563", "7546 C": "#253746", "7547 C": "#131E29", "7548 C": "#FFC600", "7577 C": "#E0592A", "7625 C": "#E1523D", "871 C": "#84754E", "872 C": "#85714D", "877 C": "#8A8D8F",
    "BLACK C": "#2D2926", "BLACK 6 C": "#101820", "PROCESS BLACK C": "#2D2926", "WHITE": "#FFFFFF", "COOL GRAY 1 C": "#D9D9D6", "COOL GRAY 4 C": "#BBBCBC", "COOL GRAY 5 C": "#B1B3B3", "COOL GRAY 7 C": "#97999B", "COOL GRAY 8 C": "#888B8D", "COOL GRAY 9 C": "#75787B", "COOL GRAY 11 C": "#53565A", "WARM GRAY 1 C": "#D7D2CB", "REFLEX BLUE C": "#001489", "PROCESS BLUE C": "#0085CA", "YELLOW C": "#FEDD00", "GREEN C": "#00AB84", "WARM RED C": "#F9423A", "RUBINE RED C": "#CE0058", "RHODAMINE RED C": "#E10098", "PURPLE C": "#BB29BB", "ORANGE 021 C": "#FE5000", "RED 032 C": "#EF3340",
  };
  function parseTarget(raw) {
    const s = String(raw || "").trim();
    if (!s) return null;
    const hx = s.replace(/^#/, "");
    if (/^[0-9a-f]{6}$/i.test(hx) || /^[0-9a-f]{3}$/i.test(hx)) {
      const full = hx.length === 3 ? hx.split("").map((c) => c + c).join("") : hx;
      return { hex: "#" + full.toUpperCase(), label: "#" + full.toUpperCase() };
    }
    let k = s.toUpperCase().replace(/PANTONE|PMS/g, "").replace(/\s+/g, " ").trim();
    k = k.replace(/\s+C$/, "").replace(/(\d)C$/, "$1").trim();
    if (k !== "WHITE") k = k + " C";
    if (PMS[k]) return { hex: PMS[k], label: "PMS " + k };
    const hit = Object.entries(C).find(([, v]) => v[2] && v[2].toUpperCase() === k);
    if (hit) return { hex: hit[1][0], label: "PMS " + k };
    return { err: `We don't have ${h(s)} on file. Type its hex instead, e.g. #C8102E.` };
  }
  const lab = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    const f = (c) => { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    const r = f(n >> 16 & 255), g = f(n >> 8 & 255), b = f(n & 255);
    const x = (r * .4124 + g * .3576 + b * .1805) / .95047, y = r * .2126 + g * .7152 + b * .0722, z = (r * .0193 + g * .1192 + b * .9505) / 1.08883;
    const q = (t) => t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
    return [116 * q(y) - 16, 500 * (q(x) - q(y)), 200 * (q(y) - q(z))];
  };
  const dE = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

  /* ── sizes ── */
  const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "Kids", "One size"];
  const SIZE_MAP = { Small: "S", Medium: "M", Large: "L", XXL: "2XL", XXXL: "3XL", OSFM: "One size", "No size": "One size" };
  function normSizes(list) {
    const out = new Set();
    list.forEach((raw) => {
      const s = raw.trim(); if (!s) return;
      if (/yrs/i.test(s)) return out.add("Kids");
      if (/\//.test(s) && !/yrs/i.test(s)) return s.split("/").forEach((p) => out.add(SIZE_MAP[p.trim()] || p.trim()));
      out.add(SIZE_MAP[s] || s);
    });
    return out;
  }
  const sizeRange = (set) => {
    const adult = SIZE_ORDER.slice(0, 9).filter((s) => set.has(s));
    if (adult.length) return adult.length === 1 ? adult[0] : `${adult[0]}–${adult[adult.length - 1]}`;
    if (set.has("Kids")) return "Kids sizes";
    return set.has("One size") ? "One size" : "";
  };

  /* ── your-logo mark: ink follows the garment colour ──
     Named colours use the map above; anything else (two-tones, prints, new names) is read off the
     photo at the print spot, once per image. */
  // 2026-10-09: centres of the fallback zones on the standardised photos (a fitted zone samples its own centre).
  const SPOT = { chest: [0.5, 0.313], "chest-hoodie": [0.5, 0.381], "chest-vest": [0.5, 0.365], "chest-l": [0.598, 0.275], "chest-l-hood": [0.596, 0.316], tube: [0.5, 0.433], cap: [0.5, 0.459], visor: [0.5, 0.334], "flap-cap": [0.5, 0.27], beanie: [0.5, 0.725], "brim-hat": [0.5, 0.271], "brim-hat-high": [0.5, 0.212], "brim-hat-low": [0.5, 0.357], bucket: [0.5, 0.359] };
  const spotCache = {};
  function readSpot(src, at) {
    const key = src + "|" + String(at);
    if (!spotCache[key]) spotCache[key] = new Promise((done) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => {
        try {
          const cv = document.createElement("canvas"); cv.width = cv.height = 64;
          const cx = cv.getContext("2d", { willReadFrequently: true }); cx.drawImage(im, 0, 0, 64, 64);
          const [fx, fy] = Array.isArray(at) ? at : SPOT[at] || SPOT.chest;
          const d = cx.getImageData(Math.round(fx * 64) - 5, Math.round(fy * 64) - 4, 10, 8).data;
          let r = 0, g = 0, b = 0, n = 0;
          for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
          done([r / n, g / n, b / n]);
        } catch { done(null); }
      };
      im.onerror = () => done(null);
      im.src = src.replace(/([?&])width=\d+/, "$1width=96") + (/[?&]width=/.test(src) ? "" : (src.includes("?") ? "&" : "?") + "width=96");
    });
    return spotCache[key];
  }
  function tintZone(c) {
    if (!c.zone || !window.NelsonMark) return;
    if (!zonesShown()) { c.tintLater = true; return; }
    c.tintLater = false;
    const name = c.cur;
    const paint = (garment) => { const p = NelsonMark.pick(garment); c.zone.style.setProperty("--nx-ink", p.ink); c.zone.style.setProperty("--nx-acc", p.acc); };
    paint(colour(name).hexes[0]);
    if (C[name]) return;
    const b = c.sws.find((x) => x.dataset.c === name);
    const src = (b && b.dataset.img) || (c.img && c.img.currentSrc);
    const at = (c.zone.className.match(/pbc-zone--([\w-]+)/) || [])[1];
    if (src) readSpot(src, c.spot || at).then((rgb) => { if (rgb && c.cur === name) paint(rgb); });
  }

  /* ── 2026-10-09 your-logo zones fitted to the standardised photos ──
     Every catalogue photo is a 1600 px square with the product centred, an 8% margin, white and a soft shadow.
     The mark is placed on the garment's own outline, measured off the card photo (the shadow is ignored by
     mirroring the top edge, because the product is centred), so caps, beanies and hats of different heights all
     land on the same spot. by "h" = sized off the garment height (apparel), "w" = off its width (headwear);
     w = mark width, fy = mark centre down the outline, dx = offset from the centre line (left chest is image-right),
     mh = max mark height (share of the outline height); crown 2 (brim hats) = small mark centred on the crown front,
     sized and placed off the crown width measured just under the crown top (chin cords make the outline height
     useless); crown 1 (bucket hats) = find where the brim flares out and centre the mark between crown top and band. Same table as piobox-pdp-engine.js.
     Values: docs/catalogue-setup-standard.md. */
  const FIT = {
    chest: { by: "h", w: 0.137, fy: 0.28 }, "chest-hoodie": { by: "h", w: 0.137, fy: 0.36 }, "chest-vest": { by: "h", w: 0.137, fy: 0.34 },
    "chest-l": { by: "h", w: 0.095, fy: 0.235, dx: 0.115 }, "chest-l-hood": { by: "h", w: 0.095, fy: 0.28, dx: 0.115 },
    tube: { by: "w", w: 0.3, fy: 0.42 },
    cap: { by: "w", w: 0.38, fy: 0.45 }, visor: { by: "w", w: 0.27, fy: 0.22, mh: 0.24 }, "flap-cap": { by: "w", w: 0.34, fy: 0.225 },
    beanie: { by: "w", w: 0.32, fy: 0.775, mh: 0.17 },
    "brim-hat": { crown: 2, w: 0.36 }, "brim-hat-high": { crown: 2, w: 0.36 }, "brim-hat-low": { crown: 2, w: 0.36 }, bucket: { crown: 1, w: 0.38, cbmax: 0.55 },
  };
  const outlineCache = {};
  function outline(src) {
    if (!outlineCache[src]) outlineCache[src] = new Promise((done) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => idle(() => {
        try {
          const N = 160, cv = document.createElement("canvas"); cv.width = cv.height = N;
          const cx = cv.getContext("2d", { willReadFrequently: true });
          const s = Math.min(N / im.naturalWidth, N / im.naturalHeight), w = im.naturalWidth * s, h = im.naturalHeight * s;
          cx.fillStyle = "#fff"; cx.fillRect(0, 0, N, N); cx.drawImage(im, (N - w) / 2, (N - h) / 2, w, h);
          const d = cx.getImageData(0, 0, N, N).data;
          const on = (x, y) => { const i = (y * N + x) * 4, lo = Math.min(d[i], d[i + 1], d[i + 2]); return 255 - lo > 28 || Math.max(d[i], d[i + 1], d[i + 2]) - lo > 18; };
          let x0 = N, y0 = N, x1 = -1, y1 = -1;
          for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) if (on(x, y)) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
          if (x1 <= x0 || y1 <= y0) return done(null);
          x1++; y1++;
          if (Math.abs((x0 + x1) / 2 - N / 2) < N * 0.02 && y1 > N - y0) y1 = N - y0;
          const rows = [];
          for (let i = 0; i < 60; i++) {
            const y = Math.min(N - 1, Math.round(y0 + (i / 60) * (y1 - y0)));
            let a = -1, b = -1;
            for (let x = 0; x < N; x++) if (on(x, y)) { if (a < 0) a = x; b = x; }
            rows.push(a < 0 ? 0 : (b - a + 1) / (x1 - x0));
          }
          const o = [x0 / N, y0 / N, (x1 - x0) / N, (y1 - y0) / N];
          o.rows = rows;
          done(o[2] > 0.2 && o[3] > 0.2 ? o : null);
        } catch { done(null); }
      });
      im.onerror = () => done(null);
      im.src = src.replace(/([?&])width=\d+/, "$1width=160") + (/[?&]width=/.test(src) ? "" : (src.includes("?") ? "&" : "?") + "width=160");
    });
    return outlineCache[src];
  }
  function markOn(f, o) {
    const [x0, y0, w, h] = o, S = f.by === "h" ? h : w;
    let mw = f.w * S, mh = mw * 0.56, cy;
    if (f.crown === 2) {
      const R = o.rows || [], A = R.filter((v, i) => (i / R.length) * h >= 0.02 && (i / R.length) * h <= 0.07);
      const cwi = (A.length ? Math.max(...A) : 0.5) * w, lo = y0 + 0.15 * cwi, hi = y0 + 0.66 * cwi;
      mw = f.w * cwi; mh = mw * 0.56;
      if (mh > (hi - lo) * 0.9) { mh = (hi - lo) * 0.9; mw = mh / 0.56; }
      cy = (lo + hi) / 2;
    } else if (f.crown) {
      const R = o.rows || [];
      let cw = 0.5, cb = f.cbmax || 0.62;
      if (R.length) {
        const A = R.filter((v, i) => i / R.length >= 0.1 && i / R.length <= 0.22).sort((a, b) => a - b);
        if (A.length) cw = A[A.length >> 1];
        for (let i = 0; i < R.length; i++) if (i / R.length > 0.15 && R[i] > cw * 1.2) { cb = i / R.length; break; }
        cb = Math.max(f.cbmin || 0.27, Math.min(cb, f.cbmax || 0.62));
      }
      const cwi = cw * w, lo = y0 + 0.3 * cb * h, hi = y0 + cb * h - 0.16 * cwi;
      mw = f.w * cwi; mh = mw * 0.56;
      if (mh > (hi - lo) * 0.9) { mh = (hi - lo) * 0.9; mw = mh / 0.56; }
      cy = (lo + hi) / 2;
    } else {
      if (f.mh && mh > f.mh * h) { mh = f.mh * h; mw = mh / 0.56; }
      cy = y0 + f.fy * h;
    }
    return [x0 + w / 2 + (f.dx || 0) * S, cy, mw, mh];
  }
  function fitZone(c) {
    const f = c.zone && FIT[c.at], src = c.img && (c.img.currentSrc || c.img.getAttribute("src"));
    if (!f || !src) return;
    if (!zonesShown()) { c.fitLater = true; return; }
    c.fitLater = false;
    outline(src).then((o) => {
      if (!o || (c.img.currentSrc || c.img.getAttribute("src")) !== src) return;
      const [cx, cy, mw, mh] = markOn(f, o), px = mw * 0.16, py = mh * 0.22, pc = (v) => (v * 100).toFixed(2) + "%";
      Object.assign(c.zone.style, { left: pc(cx - mw / 2 - px), top: pc(cy - mh / 2 - py), width: pc(mw + 2 * px), height: pc(mh + 2 * py), padding: `${pc(py)} ${pc(px)}` });
      const moved = !c.spot || Math.abs(c.spot[0] - cx) + Math.abs(c.spot[1] - cy) > 0.01;
      c.spot = [cx, cy];
      if (moved && c.zone.firstElementChild && c.zone.firstElementChild.tagName.toLowerCase() === "svg") tintZone(c);
    });
  }

  /* ── cards → data ── */
  const RUSH_MIN = parseInt(root.dataset.rushMin, 10) || 500;
  const PAGE = parseInt(root.dataset.page, 10) || 24;
  const QUOTE_URL = root.dataset.quoteUrl || "/pages/quote";
  const grid = $("[data-pbc-grid]");
  const vatMode = () => document.documentElement.getAttribute("data-pbx-vat") === "excl" ? "excl" : "incl";

  const cards = $$("[data-pbc-card]").map((el) => {
    const d = el.dataset;
    const tags = (d.tags || "").split("|").filter(Boolean);
    const sws = $$(".pbc-sw", el);
    const cols = sws.map((b) => b.dataset.c);
    sws.forEach((b) => { b.style.background = paintBg(colour(b.dataset.c)); });
    const fams = new Set(cols.map((c) => colour(c).fam).filter(Boolean));
    const sizes = normSizes((d.sizes || "").split("|"));
    const men = tags.includes("men"), women = tags.includes("women"), kids = tags.includes("kids") || tags.includes("kids-collection") || sizes.has("Kids") && !SIZE_ORDER.slice(0, 9).some((s) => sizes.has(s));
    const fit = kids ? "Kids" : men && women ? "Unisex" : men ? "Men's" : women ? "Women's" : "";
    const gsm = parseInt(d.gsm, 10) || 0;
    const c = {
      el, i: +d.i, tags, sws, cols, fams, sizes, fit,
      price: parseInt(d.price, 10) || 0, created: parseInt(d.created, 10) || 0,
      rush: (parseInt(d.stock, 10) || 0) >= RUSH_MIN,
      sleeve: d.sleeve || "", neck: d.neck || "", fabric: d.fabric || "",
      weight: !gsm ? "" : gsm < 160 ? "Light · under 160 gsm" : gsm <= 200 ? "Mid · 160–200 gsm" : "Heavy · over 200 gsm",
      deco: (d.deco || "").split("|").filter(Boolean), vendor: d.vendor || "",
      season: [tags.includes("summer-collection") && "Summer", tags.includes("winter-collection") && "Winter"].filter(Boolean),
      isNew: d.new === "true", cur: (sws.find((b) => b.getAttribute("aria-pressed") === "true") || sws[0])?.dataset.c || "",
      img: $(".pbc-card__img img", el), imgWrap: $(".pbc-card__img", el), curEl: $("[data-pbc-cur]", el), more: $("[data-pbc-more]", el), match: null,
    };
    const meta = [cols.length ? `${cols.length} colour${cols.length === 1 ? "" : "s"}` : "", sizeRange(sizes), fit].filter(Boolean).join(" · ");
    const m = $("[data-pbc-meta]", el); if (m) m.textContent = meta;
    if (c.rush) $("[data-pbc-rushbadge]", el)?.removeAttribute("hidden");
    c.zone = $(".pbc-zone", el);
    c.at = c.zone ? (c.zone.className.match(/pbc-zone--([\w-]+)/) || [])[1] : null;
    if (c.zone && c.img) { c.img.addEventListener("load", () => fitZone(c)); if (c.img.complete && c.img.naturalWidth) fitZone(c); }
    return c;
  });

  const catchUp = () => { if (!zonesShown()) return; cards.forEach((c) => idle(() => { if (c.fitLater) fitZone(c); if (c.tintLater) tintZone(c); })); };
  if (zoneMQ.addEventListener) zoneMQ.addEventListener("change", catchUp); else if (zoneMQ.addListener) zoneMQ.addListener(catchUp);
  // The your-logo mark lives in nelson-mark.js (shared with the PDP); load it beside this file.
  (window.NelsonMark ? Promise.resolve() : new Promise((done) => {
    const s = document.createElement("script");
    s.src = SELF_SRC.replace(/pb-catalogue\.js/, "nelson-mark.js");
    s.onload = s.onerror = done;
    document.head.appendChild(s);
  })).then(() => cards.forEach((c) => { if (c.zone && window.NelsonMark) { c.zone.innerHTML = NelsonMark.svg(); tintZone(c); } }));

  // A badge on every card says nothing: show Rush-ready only when it separates styles.
  if (cards.length && cards.every((c) => c.rush)) $$("[data-pbc-rushbadge]").forEach((b) => b.setAttribute("hidden", ""));

  /* ── filter groups: built from the data this collection has ── */
  const priceBand = (c) => {
    const v = (vatMode() === "excl" ? c.price / 1.15 : c.price) / 100;
    return v < 100 ? "u100" : v < 200 ? "100" : v < 400 ? "200" : "400";
  };
  const BANDS = [["u100", "Under R100"], ["100", "R100 – R200"], ["200", "R200 – R400"], ["400", "R400 and up"]];
  const uniq = (arr) => [...new Set(arr.filter(Boolean))];
  const ordered = (vals, order) => vals.sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99) || a.localeCompare(b));

  const G = [
    { id: "fam", label: "Colour family", kind: "fam", open: true, get: (c) => [...c.fams], opts: () => FAMS.filter((f) => cards.some((c) => c.fams.has(f[0]))).map((f) => [f[0], f[1]]) },
    { id: "rush", label: "Delivery", get: (c) => c.rush ? ["rush"] : [], opts: () => cards.some((c) => c.rush) && cards.some((c) => !c.rush) ? [["rush", "Rush-ready · " + (((window.NELSON_DELIVERY || {}).rushDays || "").replace(" working days", " days") || "rush")]] : [], note: "In stock at the mill in quantity, so it can go out on the rush line." },
    { id: "fit", label: "Fit", get: (c) => [c.fit], opts: () => ordered(uniq(cards.map((c) => c.fit)), ["Unisex", "Men's", "Women's", "Kids"]).map((v) => [v, v]) },
    { id: "size", label: "Size", kind: "chips", get: (c) => [...c.sizes], opts: () => SIZE_ORDER.filter((s) => cards.some((c) => c.sizes.has(s))).map((s) => [s, s]) },
    { id: "price", label: "Price per piece", sub: "blank", get: (c) => [priceBand(c)], opts: () => BANDS.filter(([k]) => cards.some((c) => priceBand(c) === k)) },
    { id: "sleeve", label: "Sleeve", get: (c) => [c.sleeve], opts: () => ordered(uniq(cards.map((c) => c.sleeve)), ["Short", "Long", "Sleeveless"]).map((v) => [v, v]) },
    { id: "neck", label: "Neckline", get: (c) => [c.neck], opts: () => ordered(uniq(cards.map((c) => c.neck)), ["Crew", "V-neck", "Collar", "Hooded", "Quarter zip", "Full zip"]).map((v) => [v, v]) },
    { id: "deco", label: "Decoration", get: (c) => c.deco, opts: () => uniq(cards.flatMap((c) => c.deco)).map((v) => [v, v]) },
    { id: "fabric", label: "Fabric", get: (c) => [c.fabric], opts: () => uniq(cards.map((c) => c.fabric)).sort().map((v) => [v, v]) },
    { id: "weight", label: "Weight", get: (c) => [c.weight], opts: () => ordered(uniq(cards.map((c) => c.weight)), ["Light · under 160 gsm", "Mid · 160–200 gsm", "Heavy · over 200 gsm"]).map((v) => [v, v]) },
    { id: "season", label: "Season", get: (c) => c.season, opts: () => ordered(uniq(cards.flatMap((c) => c.season)), ["Summer", "Winter"]).map((v) => [v, v]) },
    { id: "brand", label: "Brand", get: (c) => [c.vendor], opts: () => { const v = uniq(cards.map((c) => c.vendor)); return v.length > 1 ? v.sort().map((x) => [x, x]) : []; } },
  ];

  /* state (URL-backed) */
  const S = { type: "", sort: "featured", pms: null, shown: PAGE };
  G.forEach((g) => { S[g.id] = new Set(); });
  const params = new URLSearchParams(location.search);
  G.forEach((g) => (params.get(g.id) || "").split(",").filter(Boolean).forEach((v) => S[g.id].add(v)));
  S.type = params.get("type") || "";
  S.sort = params.get("sort") || "featured";
  const pmsIn = params.get("pms");
  function writeUrl() {
    const p = new URLSearchParams();
    G.forEach((g) => { if (S[g.id].size) p.set(g.id, [...S[g.id]].join(",")); });
    if (S.type) p.set("type", S.type);
    if (S.sort !== "featured") p.set("sort", S.sort);
    if (S.pms) p.set("pms", S.pms.raw);
    const q = p.toString();
    history.replaceState(null, "", location.pathname + (q ? "?" + q.replace(/%2C/g, ",") : ""));
  }

  const passes = (c, skip) => {
    if (S.type && !c.tags.includes(S.type)) return false;
    for (const g of G) {
      if (g.id === skip || !S[g.id].size) continue;
      const vals = g.get(c);
      if (!vals.some((v) => S[g.id].has(v))) return false;
    }
    return true;
  };

  /* ── rail rendering ── */
  const groupsEl = $("[data-pbc-groups]");
  function renderRail() {
    const opens = {};
    $$(".pbc-g", groupsEl).forEach((d) => { opens[d.dataset.g] = d.open; });
    let html = "";
    G.forEach((g) => {
      const opts = g.opts();
      if (!opts.length) return;
      const open = opens[g.id] ?? (g.open || S[g.id].size > 0);
      const count = (v) => cards.filter((c) => passes(c, g.id) && g.get(c).includes(v)).length;
      let body = "";
      if (g.kind === "fam") {
        body = `<div class="pbc-fams">${opts.map(([k, l]) => { const f = FAMS.find((x) => x[0] === k); const n = count(k); return `<button type="button" class="pbc-fam${n ? "" : " is-zero"}" data-f="fam" data-v="${k}" aria-pressed="${S.fam.has(k)}" title="${h(l)}"><i style="background:${FAM_BG[k] || f[2]}"></i><span class="pbc-sr">${h(l)}</span></button>`; }).join("")}</div>
          <label class="pbc-pms-l" for="pbc-pms">Match your brand Pantone</label>
          <form class="pbc-pms" data-pbc-pmsform><input id="pbc-pms" name="pms" autocomplete="off" placeholder="PMS 186 C or #C8102E" value="${h(S.pms?.raw || "")}"><button type="submit">Match</button></form>
          <p class="pbc-pms-h${S.pms?.err ? " is-err" : ""}" data-pbc-pmsh>${S.pms ? (S.pms.err || `<i style="background:${S.pms.hex}"></i>${h(S.pms.label)}: closest garment dyes first. <button type="button" class="pbc-link" data-pbc-pmsclear>Clear</button>`) : "Shows the closest garment dyes first. An on-screen estimate: we confirm against the physical swatch."}</p>`;
      } else if (g.kind === "chips") {
        body = `<div class="pbc-chips">${opts.map(([k, l]) => `<button type="button" class="pbc-chip${count(k) ? "" : " is-zero"}" data-f="${g.id}" data-v="${h(k)}" aria-pressed="${S[g.id].has(k)}">${h(l)}</button>`).join("")}</div>`;
      } else {
        body = opts.map(([k, l]) => { const n = count(k); return `<label class="pbc-opt${n ? "" : " is-zero"}"><input type="checkbox" data-f="${g.id}" data-v="${h(k)}"${S[g.id].has(k) ? " checked" : ""}> ${h(l)}<span class="n">${n}</span></label>`; }).join("") + (g.note ? `<p class="pbc-pms-h">${h(g.note)}</p>` : "");
      }
      const on = S[g.id].size ? `<small>${S[g.id].size} on</small>` : g.sub ? `<small>${g.sub}, ${vatMode() === "excl" ? "ex" : "incl"} VAT</small>` : "";
      html += `<details class="pbc-g" data-g="${g.id}"${open ? " open" : ""}><summary>${h(g.label)}${on}</summary><div class="pbc-g__b">${body}</div></details>`;
    });
    setHTML(groupsEl, html);
  }

  /* ── apply ── */
  function setColour(c, name, label) {
    const b = c.sws.find((x) => x.dataset.c === name);
    if (!b) return;
    c.sws.forEach((x) => x.setAttribute("aria-pressed", String(x === b)));
    c.cur = name;
    tintZone(c);
    if (c.curEl) c.curEl.innerHTML = label || h(name);
    const src = b.dataset.img;
    if (c.img && src && c.img.getAttribute("src") !== src) {
      c.imgWrap.classList.add("is-swap");
      const pre = new Image();
      pre.onload = pre.onerror = () => { c.img.removeAttribute("srcset"); c.img.removeAttribute("sizes"); c.img.src = src; c.imgWrap.classList.remove("is-swap"); };
      pre.src = src;
    }
  }
  function arrangeSwatches(c, first) {
    const lead = c.sws.filter((b) => first.includes(b.dataset.c));
    const rest = c.sws.filter((b) => !first.includes(b.dataset.c));
    const order = [...lead, ...rest];
    const wrap = order[0]?.parentNode;
    if (wrap) {
      const now = [...wrap.children].filter((x) => x.classList.contains("pbc-sw"));
      if (now.length !== order.length || now.some((x, i) => x !== order[i])) order.forEach((b) => wrap.insertBefore(b, c.more || null));
    }
    const max = 9;
    order.forEach((b, i) => b.classList.toggle("is-hide", i >= max && b.getAttribute("aria-pressed") !== "true"));
    if (c.more) { const n = order.length - max; setHidden(c.more, n <= 0); setText(c.more, n > 0 ? `+${n}` : ""); const al = `${n} more colours`; if (c.more.getAttribute("aria-label") !== al) c.more.setAttribute("aria-label", al); }
  }

  function apply(opts = {}) {
    const famOn = S.fam.size > 0;
    const tgt = S.pms && !S.pms.err ? lab(S.pms.hex) : null;
    let visible = [];
    cards.forEach((c) => {
      c.match = null;
      if (tgt) {
        let best = null;
        c.cols.forEach((n) => { const col = colour(n); if (col.print || col.multi) return; const d = dE(lab(col.hexes[0]), tgt); if (!best || d < best.d) best = { n, d }; });
        c.match = best;
      }
      const ok = passes(c);
      setHidden(c.el, !ok);
      if (!ok) return;
      visible.push(c);
      if (c.match) {
        const how = c.match.d < 8 ? "close match" : c.match.d < 20 ? "near" : "nearest we have";
        setColour(c, c.match.n, `${h(c.match.n)} · <em>${how}</em>`);
        arrangeSwatches(c, [c.match.n]);
      } else if (famOn) {
        const inFam = c.cols.filter((n) => S.fam.has(colour(n).fam));
        if (!inFam.includes(c.cur) && inFam.length) setColour(c, inFam[0]);
        else setText(c.curEl, c.cur);
        arrangeSwatches(c, inFam);
      } else {
        setText(c.curEl, c.cur);
        arrangeSwatches(c, [c.cur]);
      }
    });
    const by = {
      featured: (a, b) => a.i - b.i,
      "price-asc": (a, b) => a.price - b.price || a.i - b.i,
      "price-desc": (a, b) => b.price - a.price || a.i - b.i,
      colours: (a, b) => b.cols.length - a.cols.length || a.i - b.i,
      new: (a, b) => b.created - a.created || a.i - b.i,
    };
    const sortFn = tgt && S.sort === "featured" ? (a, b) => (a.match?.d ?? 999) - (b.match?.d ?? 999) : by[S.sort] || by.featured;
    visible.sort(sortFn);
    // reorder with the fewest moves (none when the order is unchanged)
    const want = [...visible, ...cards.filter((c) => c.el.hidden)].map((c) => c.el);
    const now = [...grid.children].filter((x) => x.hasAttribute("data-pbc-card"));
    if (now.length !== want.length || now.some((x, i) => x !== want[i])) {
      let ref = now[0] || null;
      if (ref && ref.parentNode === grid && ref.nextElementSibling && now.length && now[now.length - 1].nextElementSibling) ref = null; // cards not at the end: fall back to appending
      for (const el of want) {
        if (el === ref) { ref = ref.nextElementSibling; continue; }
        grid.insertBefore(el, ref);
      }
    }
    visible.forEach((c, i) => c.el.classList.toggle("is-later", i >= S.shown));
    const reveal = $("[data-pbc-reveal]");
    setHidden(reveal, visible.length <= S.shown);
    setText(reveal, `Show more styles (${visible.length - Math.min(S.shown, visible.length)})`);
    $$("[data-pbc-count]").forEach((el) => setText(el, visible.length));
    setHidden($("[data-pbc-empty]"), visible.length > 0);
    $$(".pbc-tile").forEach((t) => { const v = String(t.dataset.type === S.type); if (t.getAttribute("aria-pressed") !== v) t.setAttribute("aria-pressed", v); });
    $$("[data-pbc-typeclear]").forEach((b) => setHidden(b, !S.type)); // 2026-10-09: Clear only while a type is selected
    $("[data-pbc-sort]").value = S.sort;
    const a = document.activeElement;
    const keep = a && groupsEl.contains(a) && a.dataset.f ? `[data-f="${a.dataset.f}"][data-v="${CSS.escape(a.dataset.v)}"]` : null;
    renderPills();
    renderRail();
    if (keep) groupsEl.querySelector(keep)?.focus();
    writeUrl();
  }

  function renderPills() {
    const pills = [];
    G.forEach((g) => S[g.id].forEach((v) => {
      const label = (g.opts().find(([k]) => k === v) || [v, v])[1];
      const dot = g.id === "fam" ? `<i style="background:${FAM_BG[v] || (FAMS.find((f) => f[0] === v) || [])[2]}"></i>` : "";
      pills.push(`<button type="button" class="pbc-pill" data-rm="${g.id}" data-v="${h(v)}" aria-label="Remove ${h(label)}">${dot}${h(label)} <span aria-hidden="true">×</span></button>`);
    }));
    if (S.pms && !S.pms.err) pills.push(`<button type="button" class="pbc-pill" data-rm="pms" aria-label="Remove Pantone match"><i style="background:${S.pms.hex}"></i>${h(S.pms.label)} <span aria-hidden="true">×</span></button>`);
    setHTML($("[data-pbc-pills]"), pills.join(""));
    const n = G.reduce((a, g) => a + S[g.id].size, 0) + (S.pms && !S.pms.err ? 1 : 0);
    const badge = $("[data-pbc-active]");
    setHidden(badge, !n); setText(badge, n || "");
  }

  /* ── events ── */
  root.addEventListener("click", (e) => {
    const t = e.target;
    const sw = t.closest(".pbc-sw");
    if (sw) { const c = cards.find((x) => x.el.contains(sw)); if (c) setColour(c, sw.dataset.c); return; }
    const f = t.closest("button[data-f]");
    if (f) {
      const set = S[f.dataset.f]; set.has(f.dataset.v) ? set.delete(f.dataset.v) : set.add(f.dataset.v);
      if (f.dataset.f === "fam") S.pms = null; // one colour question at a time: family or Pantone
      S.shown = PAGE; apply(); return;
    }
    const rm = t.closest("[data-rm]");
    if (rm) { if (rm.dataset.rm === "pms") S.pms = null; else S[rm.dataset.rm].delete(rm.dataset.v); apply({ reset: true }); return; }
    if (t.closest("[data-pbc-pmsclear]")) { S.pms = null; apply({ reset: true }); return; }
    if (t.closest("[data-pbc-clear]")) { G.forEach((g) => S[g.id].clear()); S.type = ""; S.pms = null; S.shown = PAGE; apply({ reset: true }); return; }
    if (t.closest("[data-pbc-typeclear]")) { S.type = ""; S.shown = PAGE; apply(); return; }
    const tile = t.closest(".pbc-tile");
    if (tile) { S.type = tile.dataset.type === S.type ? "" : tile.dataset.type; S.shown = PAGE; apply(); return; }
    if (t.closest("[data-pbc-reveal]")) { S.shown += PAGE; apply(); return; }
    if (t.closest("[data-pbc-open]")) { sheet(true); return; }
    if (t.closest("[data-pbc-close]")) { sheet(false); return; }
    const q = t.closest("[data-pbc-quote]");
    if (q) { addQuote(cards.find((x) => x.el.contains(q)), q); }
  });
  root.addEventListener("change", (e) => {
    const cb = e.target.closest("input[data-f]");
    if (cb) { const set = S[cb.dataset.f]; cb.checked ? set.add(cb.dataset.v) : set.delete(cb.dataset.v); S.shown = PAGE; apply(); return; }
    if (e.target.matches("[data-pbc-sort]")) { S.sort = e.target.value; apply(); }
  });
  root.addEventListener("submit", (e) => {
    const form = e.target.closest("[data-pbc-pmsform]");
    if (!form) return;
    e.preventDefault();
    const raw = form.pms.value.trim();
    if (!raw) { S.pms = null; apply({ reset: true }); return; }
    const r = parseTarget(raw);
    S.pms = r ? { ...r, raw } : null;
    if (S.pms && !S.pms.err) S.fam.clear();
    S.shown = PAGE;
    apply();
    setTimeout(() => $("#pbc-pms")?.focus(), 0);
  });
  window.addEventListener("pbx:vat-mode", () => apply());

  /* mobile sheet */
  const rail = $("[data-pbc-rail]"), scrim = $(".pbc-scrim");
  function sheet(open) {
    rail.classList.toggle("is-open", open);
    scrim.hidden = !open; scrim.classList.toggle("is-open", open);
    $("[data-pbc-open]").setAttribute("aria-expanded", String(open));
    document.documentElement.style.overflow = open ? "hidden" : "";
    if (open) rail.querySelector("button, summary, input")?.focus();
  }
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && rail.classList.contains("is-open")) sheet(false); });

  /* desktop: the filter column folds away; the grid takes the room (remembered per browser) */
  const foldBtn = $("[data-pbc-fold]");
  function fold(on) {
    root.classList.toggle("is-folded", on);
    if (foldBtn) { foldBtn.setAttribute("aria-expanded", String(!on)); $("[data-pbc-foldlabel]").textContent = on ? "Show filters" : "Hide filters"; }
    try { localStorage.setItem("pbc-folded", on ? "1" : "0"); } catch {}
  }
  foldBtn?.addEventListener("click", () => fold(!root.classList.contains("is-folded")));
  try { if (localStorage.getItem("pbc-folded") === "1") fold(true); } catch {}

  /* add to quote: the line lands on the quote page with sizes and branding to confirm */
  let toastT;
  function toast(html) {
    const t = $("[data-pbc-toast]");
    t.innerHTML = html; t.hidden = false;
    clearTimeout(toastT); toastT = setTimeout(() => { t.hidden = true; }, 4200);
  }
  function addQuote(c, btn) {
    if (!c) return;
    if (!window.PBQ) { location.href = QUOTE_URL; return; }
    const b = c.sws.find((x) => x.dataset.c === c.cur);
    const line = {
      brand: "piobox", id: "PB" + Date.now().toString(36).toUpperCase(),
      handle: c.el.dataset.handle, title: c.el.dataset.title, url: c.el.dataset.url,
      colour: c.cur, image: (b && b.dataset.img) || c.img?.currentSrc || "",
      sizes: {}, sizeText: "Sizes to confirm", total: 0,
      print: "Branding to confirm", method: "", artwork: "To follow", rows: [],
      notes: ["Added from the catalogue: add quantities and sizes, or open the product to put your logo on it."],
      estimate: null, edited: false, ts: Date.now(),
    };
    if (!PBQ.add("pb", line)) { toast("Your browser blocked saving the quote. Try a normal window."); return; }
    btn.classList.add("is-done");
    setTimeout(() => btn.classList.remove("is-done"), 1400);
    const n = PBQ.count();
    toast(`${h(line.title)} in ${h(line.colour)} is on your quote · ${n} item${n === 1 ? "" : "s"}. <a href="${h(QUOTE_URL)}">View quote →</a>`);
  }

  /* first paint */
  if (pmsIn) { const r = parseTarget(pmsIn); S.pms = r ? { ...r, raw: pmsIn } : null; }
  apply();
})();
